import { useState } from "react";
import { useLocation } from "wouter";
import { Compass, Trophy, Check, Lock, Star, Gift, AlertCircle, Lightbulb, MessageSquare } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useMissions } from "@/hooks/useMissions";
import {
  CATEGORY_LABELS,
  MISSIONS,
  type Mission,
  type MissionCategory,
  pointsForNextLevel,
} from "@/data/missions";

function reportMissionStuck(m: Mission) {
  window.dispatchEvent(
    new CustomEvent("marketlink:open-feedback", {
      detail: {
        category: "bug",
        prefill: `Stuck on mission "${m.title}".\nHint shown: ${m.help ?? m.blurb}\n\nWhat happened: `,
      },
    }),
  );
}

function openGeneralFeedback() {
  window.dispatchEvent(new CustomEvent("marketlink:open-feedback", { detail: {} }));
}

export function MissionsWidget() {
  const [open, setOpen] = useState(false);
  const [reportPickerOpen, setReportPickerOpen] = useState(false);
  const [betaHubOpen, setBetaHubOpen] = useState(false);
  const [, setLocation] = useLocation();
  const {
    missions,
    progressMap,
    totalPoints,
    level,
    activeId,
    setActive,
    completeMission,
  } = useMissions();

  const activeMission = activeId ? MISSIONS.find((m) => m.id === activeId) : null;
  const nextLevelPts = pointsForNextLevel(level);
  const prevLevelPts = level === 0 ? 0 : Math.pow(level, 2) * 10;
  const progressPct =
    nextLevelPts > prevLevelPts
      ? Math.min(100, ((totalPoints - prevLevelPts) / (nextLevelPts - prevLevelPts)) * 100)
      : 0;

  const grouped: Record<MissionCategory, Mission[]> = {
    buyer: [],
    seller: [],
    messaging: [],
    profile: [],
    loop: [],
    bug_hunt: [],
  };
  for (const m of missions) grouped[m.category].push(m);

  function handleStart(m: Mission) {
    // Don't auto-navigate. Testers learn the app better by finding
    // the screen themselves. The active-mission strip shows the hint
    // text + a "Show me where" escape hatch if they're stuck.
    setActive(m.id);
    setOpen(false);
  }

  function handleShowMe(m: Mission) {
    if (m.startRoute) setLocation(m.startRoute);
  }

  function handleManualDone(m: Mission) {
    completeMission(m);
    if (activeId === m.id) setActive(null);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setBetaHubOpen(true)}
        className="fixed right-4 bottom-24 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-amber-500 text-white shadow-lg transition hover:bg-amber-600"
        aria-label="Beta tools"
        data-testid="missions-widget-trigger"
      >
        <Compass className="h-5 w-5" />
      </button>

      <Dialog open={betaHubOpen} onOpenChange={setBetaHubOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Beta tools</DialogTitle>
            <DialogDescription>
              Run a mission against the demo marketplace, or send feedback straight to the product owner.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <button
              type="button"
              onClick={() => {
                setBetaHubOpen(false);
                setOpen(true);
              }}
              className="flex items-start gap-3 rounded-lg border p-3 text-left transition hover:border-primary/60 hover:bg-accent"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
                <Compass className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <div className="text-sm font-semibold">Beta missions</div>
                <p className="text-xs text-muted-foreground">
                  Tasks against the demo marketplace. Earn XP and rewards.
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setBetaHubOpen(false);
                openGeneralFeedback();
              }}
              className="flex items-start gap-3 rounded-lg border p-3 text-left transition hover:border-primary/60 hover:bg-accent"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <MessageSquare className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-sm font-semibold">Beta feedback</div>
                <p className="text-xs text-muted-foreground">
                  Send a bug, idea, or question — your nav and any errors are attached automatically.
                </p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {activeMission && (
        <div className="fixed inset-x-0 bottom-[3.75rem] z-30 border-t-2 border-primary/40 bg-card shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3">
          <div className="mx-auto flex max-w-md flex-col gap-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                Active mission
              </div>
              <div className="text-sm font-semibold text-foreground">
                {activeMission.title}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {activeMission.help ?? activeMission.blurb}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Button size="sm" className="flex-1" onClick={() => handleManualDone(activeMission)}>
                I did this
              </Button>
              {activeMission.startRoute && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleShowMe(activeMission)}
                >
                  Show me where
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReportPickerOpen(true)}
              >
                Report
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setActive(null)}>
                Abandon
              </Button>
            </div>
          </div>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Beta Missions
            </SheetTitle>
            <SheetDescription>
              Test against demo sellers, find bugs, earn XP.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-1 rounded-lg border bg-muted/40 p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Level {level}</span>
              <span className="text-muted-foreground">
                {totalPoints} / {nextLevelPts} XP
              </span>
            </div>
            <Progress value={progressPct} className="h-2" />
          </div>

          <div className="mt-6 space-y-6">
            {(Object.keys(grouped) as MissionCategory[]).map((cat) => {
              const list = grouped[cat];
              if (list.length === 0) return null;
              return (
                <div key={cat}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {CATEGORY_LABELS[cat]}
                  </h3>
                  <div className="space-y-2">
                    {list.map((m) => {
                      const p = progressMap[m.id];
                      const isCompleted = p?.status === "completed";
                      const isActive = activeId === m.id;
                      return (
                        <div
                          key={m.id}
                          className={`rounded-md border p-3 ${
                            isActive ? "border-primary bg-primary/5" : ""
                          } ${isCompleted ? "opacity-70" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-1 text-sm font-medium">
                                {isCompleted && <Check className="h-3.5 w-3.5 text-brand-secondary" />}
                                {m.realReward && <Gift className="h-3.5 w-3.5 text-amber-500" />}
                                <span>{m.title}</span>
                              </div>
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                {m.blurb}
                              </p>
                            </div>
                            <Badge variant="secondary" className="shrink-0 text-xs">
                              <Star className="mr-1 h-3 w-3" />
                              {m.points}
                            </Badge>
                          </div>
                          <div className="mt-2 flex gap-2">
                            {isCompleted ? (
                              <span className="text-xs text-muted-foreground">Completed</span>
                            ) : isActive ? (
                              <span className="text-xs font-medium text-primary">Active</span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleStart(m)}
                              >
                                Start
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {missions.every((m) => progressMap[m.id]?.status === "completed") && (
            <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-center text-sm">
              <Lock className="mx-auto mb-2 h-5 w-5 text-amber-500" />
              You finished every mission. Mark will be in touch about your reward.
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={reportPickerOpen} onOpenChange={setReportPickerOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>What do you want to report?</DialogTitle>
            <DialogDescription>
              Tell us about this specific mission, or send a general bug or idea.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <button
              type="button"
              onClick={() => {
                if (activeMission) reportMissionStuck(activeMission);
                setReportPickerOpen(false);
              }}
              className="flex items-start gap-3 rounded-lg border p-3 text-left transition hover:border-primary/60 hover:bg-accent"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
                <AlertCircle className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-sm font-semibold">This mission is stuck</div>
                <p className="text-xs text-muted-foreground">
                  Send a bug report pre-filled with the mission and the hint shown.
                </p>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                openGeneralFeedback();
                setReportPickerOpen(false);
              }}
              className="flex items-start gap-3 rounded-lg border p-3 text-left transition hover:border-primary/60 hover:bg-accent"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
                <Lightbulb className="h-4 w-4 text-amber-600" />
              </div>
              <div>
                <div className="text-sm font-semibold">Bug or idea (something else)</div>
                <p className="text-xs text-muted-foreground">
                  Open the general feedback form — your nav, taps, and any errors are attached automatically.
                </p>
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
